/**
 * Admin Users — Core Field Definitions
 * Editable user fields: name, email, role, status, billing.
 */

import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types/shared";

export const adminUserCoreFields: Record<string, SimpleFieldConfig> = {
  first_name: {
    type: "text" as const,
    label: "First Name",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 2,
    filterType: "contains",
    width: 150,
    minWidth: 120,
    maxWidth: 250,
    required: true,
    validation: {
      required: "First name is required.",
      min: 2,
    },
    placeholder: "Enter first name",
    icon: "User",
    allowedRoles: ["wtc_admin"],
    showRowActions: true,
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "basic",
  },
  last_name: {
    type: "text" as const,
    label: "Last Name",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 2,
    filterType: "contains",
    width: 150,
    minWidth: 120,
    maxWidth: 250,
    required: true,
    validation: {
      required: "Last name is required.",
      min: 2,
    },
    placeholder: "Enter last name",
    icon: "User",
    allowedRoles: ["wtc_admin"],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "basic",
  },
  email: {
    type: "text" as const,
    label: "Email",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 3,
    filterType: "contains",
    width: 220,
    minWidth: 180,
    maxWidth: 350,
    required: true,
    validation: {
      required: "Email is required.",
      pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    },
    placeholder: "Enter email address",
    icon: "Mail",
    allowedRoles: ["wtc_admin"],
    showOnCard: true,
    cardSpan: 2,
    mobileDetailSectionId: "basic",
  },
  role: {
    type: "select" as const,
    label: "Role",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 2,
    filterType: "equals" as any,
    width: 140,
    minWidth: 120,
    maxWidth: 200,
    required: true,
    validation: {
      required: "Role is required.",
    },
    placeholder: "Select role",
    icon: "Shield",
    options: [
      { value: "wtc_admin", label: "Admin" },
      { value: "wtc_employee", label: "Employee" },
      { value: "wtc_crm", label: "CRM User" },
      { value: "client_user", label: "Client User" },
    ],
    allowedRoles: ["wtc_admin"],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "basic",
  },
  is_active: {
    type: "boolean" as const,
    label: "Active",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 1,
    filterType: "equals" as any,
    width: 100,
    minWidth: 80,
    maxWidth: 120,
    placeholder: "Active status",
    icon: "CheckCircle",
    allowedRoles: ["wtc_admin"],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "access",
  },
  available_hours: {
    type: "number" as const,
    label: "Available Hours",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 1,
    filterType: "range",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    validation: {
      min: 0,
      max: 168,
    },
    placeholder: "Hours per week",
    icon: "Clock",
    allowedRoles: ["wtc_admin"],
    mobileDetailSectionId: "access",
  },
  is_billable: {
    type: "boolean" as const,
    label: "Billable",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 1,
    filterType: "equals" as any,
    width: 100,
    minWidth: 80,
    maxWidth: 120,
    placeholder: "Billable status",
    icon: "CheckCircle",
    allowedRoles: ["wtc_admin"],
    mobileDetailSectionId: "access",
  },
  hourly_rate: {
    type: "number" as const,
    label: "Hourly Rate",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 1,
    filterType: "range",
    width: 130,
    minWidth: 110,
    maxWidth: 180,
    validation: {
      min: 0,
    },
    placeholder: "Hourly rate",
    helpText:
      "Employee's hourly pay rate. For accounting, WTC applies a 20% burden on top of this to cover software, hardware, and other per-employee costs — internal labor cost = hourly rate × 1.20.",
    icon: "DollarSign",
    allowedRoles: ["wtc_admin"],
    mobileDetailSectionId: "access",
  },
};
